import { useState } from 'react';
import clsx from 'clsx';
import { LOOSE_CHANNELS_LABEL, LOOSE_CHANNELS_OPTIONS, myLooseChannelsOptions, type LooseChannelsPosition } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useChatSections, useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { useLooseChannels } from './looseChannels';

function Choice<T extends string | null>({ value, options, onChange, disabled, testId }: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; disabled?: boolean; testId: string }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={LOOSE_CHANNELS_LABEL} data-testid={testId}>
      {options.map(o => (
        <button key={o.id ?? 'default'} type="button" role="radio" aria-checked={value === o.id} disabled={disabled} onClick={() => value !== o.id && onChange(o.id)}
          data-testid={`${testId}-${o.id ?? 'default'}`}
          className={clsx('min-h-[36px] rounded-md px-3 py-1.5 text-sm', value === o.id ? 'bg-accent font-semibold text-on-accent' : 'ring-1 ring-inset ring-slate-300 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** t446u: Organise sections › the workspace default (people who organise sections). */
export function WorkspaceLooseChannels() {
  const ws = useWorkspaceId();
  const sections = useChatSections();
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState(false);
  const value = sections.data?.loose_channels ?? 'below';
  const save = async (v: LooseChannelsPosition) => {
    if (!ws) return;
    setBusy(true);
    try {
      await api.setChatSectionsLayout(ws, { loose_channels: v });
      queryClient.setQueryData(qk.chatSections(ws), (d: object | undefined) => (d ? { ...d, loose_channels: v } : d));
      toast('Saved', 'success');
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="workspace-loose-channels">
      <span className="flex-1 text-sm font-medium">{LOOSE_CHANNELS_LABEL}</span>
      <Choice value={value} options={LOOSE_CHANNELS_OPTIONS} onChange={v => void save(v)} disabled={busy || !sections.data} testId="loose-default" />
    </div>
  );
}

/** t446u: Preferences › your own choice (or follow the workspace). */
export function MyLooseChannels() {
  const loose = useLooseChannels();
  const toast = useUi(s => s.toast);
  const choose = async (v: LooseChannelsPosition | null) => {
    try { await loose.setMine(v); toast('Saved', 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800" data-testid="my-loose-channels">
      <p className="mb-2 text-sm font-medium">{LOOSE_CHANNELS_LABEL}</p>
      <Choice value={loose.mine} options={myLooseChannelsOptions(loose.workspaceDefault)} onChange={v => void choose(v)} disabled={!loose.ready} testId="loose-mine" />
    </div>
  );
}
